
import { calculateLeagueTableStats } from './utils/calculateStats';
import { OddsAnalysisResult, OddsRange } from '@/types/teamTypes';
import { MatchData } from '@/components/team-details/types/selector-types';

export { calculateLeagueTableStats };

export const calculateOddsAnalysis = (matches: MatchData[], oddsType: 'home' | 'away'): OddsAnalysisResult => {
  // Base result structure
  const result: OddsAnalysisResult = {
    totalMatches: 0,
    ranges: []
  };

  if (!matches || matches.length === 0) {
    return result;
  }

  // Count matches with valid odds
  const matchesWithValidOdds = matches.filter(match => {
    const oddsField = oddsType === 'home' ? match.B365H : match.B365A;
    const oddsValue = typeof oddsField === 'string' ? parseFloat(oddsField) : oddsField;
    return !isNaN(oddsValue) && oddsValue > 0;
  });

  result.totalMatches = matches.length;

  // Define odds ranges
  const oddsRanges = [
    { min: 1.0, max: 1.5, label: "1.00-1.50" },
    { min: 1.51, max: 2.0, label: "1.51-2.00" },
    { min: 2.01, max: 2.5, label: "2.01-2.50" },
    { min: 2.51, max: 3.0, label: "2.51-3.00" },
    { min: 3.01, max: 3.5, label: "3.01-3.50" },
    { min: 3.51, max: 4.5, label: "3.51-4.50" },
    { min: 4.51, max: 6.0, label: "4.51-6.00" },
    { min: 6.01, max: 10.0, label: "6.01-10.00" },
    { min: 10.01, max: 100.0, label: "10.01+" }
  ];

  // Analyze each odds range
  result.ranges = oddsRanges.map(range => {
    const matchesInRange = matchesWithValidOdds.filter(match => {
      const oddsField = oddsType === 'home' ? match.B365H : match.B365A;
      const oddsValue = typeof oddsField === 'string' ? parseFloat(oddsField) : oddsField;
      return oddsValue >= range.min && oddsValue <= range.max;
    });
    
    // Count results for this range
    let wins = 0;
    let draws = 0;
    let losses = 0;
    
    matchesInRange.forEach(match => {
      if (oddsType === 'home') {
        if (match.FTR === 'H') wins++;
        else if (match.FTR === 'D') draws++;
        else if (match.FTR === 'A') losses++;
      } else {
        if (match.FTR === 'A') wins++;
        else if (match.FTR === 'D') draws++;
        else if (match.FTR === 'H') losses++;
      }
    });
    
    const oddsRange: OddsRange = {
      min: range.min,
      max: range.max,
      label: range.label,
      matches: matchesInRange.length,
      wins: wins,
      winPercent: matchesInRange.length ? (wins / matchesInRange.length * 100) : 0,
      draws: draws,
      drawPercent: matchesInRange.length ? (draws / matchesInRange.length * 100) : 0,
      losses: losses,
      lossPercent: matchesInRange.length ? (losses / matchesInRange.length * 100) : 0,
      
      // The following fields are in the OddsRange type but not needed for our calculation
      // Setting to 0 to satisfy TypeScript
      backPL: 0,
      layPL: 0,
      over25PL: 0,
      under25PL: 0,
      over25LayPL: 0,
      under25LayPL: 0
    };
    
    return oddsRange;
  }).filter(range => range.matches > 0); // Only include ranges with matches
  
  return result;
};
